import { createContext, useContext, type ReactNode } from 'react';
import { defaultSettings, type NavigationManifest, type SettingValues } from '@tbbt/shared';
import type { RouteManifestEntry } from '@tbbt/shared';
import { useSiteManifest } from '@/lib/api/queries';

interface SiteContextValue {
  navigation: NavigationManifest;
  settings: SettingValues;
  routes: RouteManifestEntry[];
  isLoading: boolean;
  /** True when the manifest could not be fetched and defaults are being used. */
  isDegraded: boolean;
}

const emptyNavigation: NavigationManifest = {
  HEADER: [],
  FOOTER_SOLUTIONS: [],
  FOOTER_COMPANY: [],
  FOOTER_LEGAL: [],
};

const SiteContext = createContext<SiteContextValue>({
  navigation: emptyNavigation,
  settings: defaultSettings(),
  routes: [],
  isLoading: true,
  isDegraded: false,
});

/**
 * Provides navigation, settings and the route table to the whole public site.
 *
 * The important behaviour is the failure mode. If the manifest request fails, this
 * falls back to the schema defaults from `@tbbt/shared` rather than rendering nothing.
 * A brand name and an empty nav is a usable page; a blank screen because one request
 * failed is not — and a marketing site going white on a transient API blip is a far
 * worse outcome than one temporarily missing its menu.
 *
 * Because the defaults are derived from the same Zod schemas the server validates
 * against, the fallback shape is guaranteed to be the shape components expect.
 */
export function SiteProvider({ children }: { children: ReactNode }): ReactNode {
  const { data, isLoading, isError } = useSiteManifest();

  const value: SiteContextValue = {
    navigation: data?.navigation ?? emptyNavigation,
    settings: data?.settings ?? defaultSettings(),
    routes: data?.routes ?? [],
    isLoading,
    isDegraded: isError,
  };

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

export function useSite(): SiteContextValue {
  return useContext(SiteContext);
}

/** Convenience accessors, so components do not reach through `settings` every time. */
export function useBrand(): SettingValues['brand'] {
  return useSite().settings.brand;
}

export function useFeatureFlags(): SettingValues['featureFlags'] {
  return useSite().settings.featureFlags;
}
